import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import { Formik, ErrorMessage } from "formik";
import * as Yup from "yup";
import { jwtDecode } from "jwt-decode";
import Card from "../../../components/Card";
import url from "../../../configs/config";
import InputField from "../../../components/input/input-field";
import AlertNotificationSimple from "../../../components/alert/alert-notification-simple";
import FocusError from "../../../components/error/error-focus";
import { roleTypes } from "../../../data/roleType";

const WarehouseAdd = () => {
  const alertRef = useRef(null);
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [decodedToken, setDecodedToken] = useState();
  const token = localStorage.getItem("coyote-token");
  const [formData, setFormData] = useState({
    name: "",
    identityNumber: "",
    address: "",
    type: null,
  });

  const [alerts, setAlerts] = useState({
    message: "",
    type: "",
    show: false,
    alertFor: "",
  });
  useEffect(() => {
    if (token && typeof token === "string") {
      try {
        const decoded = jwtDecode(token);
        setDecodedToken(decoded?.user);
      } catch (error) {
        console.error("Failed to decode token", error);
      }
    }
  }, [token]);

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    const data = {
      name: values.name,
      identityNumber: values.identityNumber,
      address: values.address,
      type: values.type,
    };
    try {
      const config = {
        headers: {
          Authorization: `${token}`,
        },
      };

      const response = await axios.post(
        `${url.backendURL}warehouse/`,
        data,
        config
      );
      setErrors({});
      if (alertRef.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      setAlerts({
        message: response.data.message,
        type: "success",
        show: true,
        alertFor: "add-warehouse",
      });
      setTimeout(() => {
        setAlerts({ show: false, alertFor: "", message: "", type: "" });
        navigate(`/dashboard/app/warehouse-list`);
      }, 2000);
    } catch (err) {
      handleError(err, "add-warehouse");
    } finally {
      setSubmitting(false);
    }
  };

  const handleError = (err, alertFor) => {
    const { response } = err;
    if (alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    if (
      response.data.error === "jwt expired" ||
      response?.data?.error === "Unauthorized. User not found"
    ) {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "expired" },
        }
      );
    } else {
      const responseErrors = response?.data?.error || {};
      setErrors(responseErrors);
      setAlerts({
        message: response ? response.data.message : "An error occurred",
        type: "danger",
        show: true,
        alertFor,
      });
      setTimeout(() => {
        setAlerts({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    }
  };

  const resetFormData = (resetForm) => {
    resetForm();
    setFormData({
      name: "",
      identityNumber: "",
      address: "",
      type: null,
    });
  };

  const validationSchema = () =>
    Yup.object().shape({
      name: Yup.string()
        .trim()
        .min(1, "Name must be at least 1 character long")
        .max(255, "name must not exceed 255 characters")
        .required("Name is required")
        .matches(
          /^(?=.*[a-zA-Z])[a-zA-Z0-9 '&.-]*$/,
          "Warehouse name can only contain letters and numbers"
        ),
      identityNumber: Yup.string()
        .trim()
        .matches(
          /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9!@#$%^&*()_+=[\]{};':"\\|,.<>/?-\s]*$/,
          "Identity number must contain at least one letter or number"
        )
        .max(100, "Identity number must not exceed 100 characters")
        .required("Identity number is required"),
      address: Yup.string()
        .trim()
        .min(1, "Address must be at least 1 character long")
        .max(255, "Address must not exceed 255 characters")
        .required("Address is required")
        .matches(
          /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9!@#$%^&*()_+=[\]{};':"\\|,.<>/?-\s]*$/,
          "Address must contain at least one letter or number"
        ),
      type: Yup.string()
        .oneOf(["physical", "van"], "Type must be either 'physical' or 'van'")
        .required("Type is required"),
    });

  return (
    <div>
      <Formik
        enableReinitialize={true}
        initialValues={{
          ...formData,
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          handleChange,
          handleBlur,
          setFieldValue,
          handleSubmit,
          isSubmitting,
          resetForm,
        }) => (
          <Form onSubmit={handleSubmit}>
            <FocusError />
            <Row>
              <Col md="12" ref={alertRef}>
                <Card>
                  <Card.Header className="d-flex justify-content-between">
                    <div
                      className="breadcrumb header-title"
                      style={{ cursor: "pointer" }}
                    >
                      <h4
                        onClick={() =>
                          navigate("/dashboard/app/warehouse-list")
                        }
                        className="text-primary card-title"
                        style={{ cursor: "pointer" }}
                      >
                        <span>Warehouse</span>
                      </h4>
                      <h4 className="mx-2 card-title">{">"}</h4>
                      <h4 className="card-title">Add</h4>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <AlertNotificationSimple
                      type={alerts.type}
                      message={alerts.message}
                      show={alerts.show}
                      onClose={() => setAlerts({ ...alerts, show: false })}
                    />
                    <div className="new-user-info">
                      <div className="row">
                        <InputField
                          className="col-md-6 form-group"
                          type="text"
                          name="name"
                          placeholder="Enter name"
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          asterick={true}
                          errorMessage={errors?.name}
                          label="Name"
                        />

                        <InputField
                          className="col-md-6 form-group"
                          type="text"
                          name="identityNumber"
                          placeholder="Enter identity number"
                          value={values.identityNumber}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          asterick={true}
                          errorMessage={errors?.identityNumber}
                          label="Identity Number"
                        />

                        <InputField
                          className="col-md-6 form-group"
                          type="text"
                          name="address"
                          placeholder="Enter address"
                          value={values.address}
                          asterick={true}
                          errorMessage={errors?.address}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          label="Address"
                        />
                        <Form.Group className="col-md-6 form-group">
                          <Form.Label htmlFor="type">
                            Warehouse Type
                            <span className="text-danger">*</span>
                          </Form.Label>
                          <Select
                            closeMenuOnScroll={(e) => {
                              try {
                                const target = e.target;

                                if (
                                  !target ||
                                  !target.nodeType ||
                                  typeof target.closest !== "function"
                                ) {
                                  return true;
                                }

                                const isMenuScroll =
                                  target.closest('[class*="MenuList"]') ||
                                  target.closest('[class*="menu"]');

                                return !isMenuScroll;
                              } catch (error) {
                                return true;
                              }
                            }}
                            id="type"
                            options={[
                              { value: "physical", label: "Physical" },
                              { value: "van", label: "Van" },
                            ]}
                            value={
                              values.type
                                ? {
                                    value: values.type,
                                    label:
                                      values.type.charAt(0).toUpperCase() +
                                      values.type.slice(1),
                                  }
                                : null
                            }
                            onChange={(option) => {
                              if (option) {
                                setFieldValue("type", option.value);
                              } else {
                                setFieldValue("type", null); // Set to null if cleared
                              }
                            }}
                            onBlur={handleBlur}
                            menuPortalTarget={document.body} // render menu in portal
                            menuPosition="fixed" // position relative to viewport
                            menuPlacement="auto"
                            styles={{
                              control: (provided) => ({
                                ...provided,
                                color: "#5e667b",
                              }),
                            }}
                            isClearable
                          />

                          <ErrorMessage
                            name="type"
                            component="div"
                            className="text-danger"
                          />
                        </Form.Group>

                        <hr />
                        <div className="d-flex flex-wrap flex-md-nowrap justify-content-end gap-1 gap-md-2">
                          <Button
                            type="button"
                            className="me-2 me-md-5 px-3 px-md-4"
                            variant="btn btn-light"
                            onClick={() => resetFormData(resetForm)}
                          >
                            Reset
                          </Button>
                          <Button
                            type="submit"
                            disabled={isSubmitting}
                            variant="btn btn-primary"
                            className="me-2 me-md-5 px-3 px-md-4"
                          >
                            {isSubmitting ? "Submitting..." : "Submit"}
                          </Button>

                          <Button
                            type="button"
                            className=" px-3 px-md-4"
                            variant="btn btn-danger"
                            onClick={() =>
                              navigate("/dashboard/app/warehouse-list")
                            }
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default WarehouseAdd;
